// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { FormFieldNumber } from 'common/components/Form/FormFieldNumber/FormFieldNumber';
import { SIZE } from 'common/constants';
import {
    Column,
    Columns,
    FormField,
    FormFieldSelect,
    Translate,
} from '@plesk/ui-library';
import { VirtualizationType } from 'common/api/resources/ComputeResource';
import { DataUnit } from 'common/helpers/units';
import {
    FIELDS,
    MAX_TOTAL_VCPU,
} from 'common/components/plan/PlanForm';
import { FORM } from 'admin/plan/constants/tests';

const CPU_TOPOLOGY_VALUE_STYLE: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
};

// @plesk/ui-library's internal error styling class (FormField applies it automatically when
// bound to a non-empty Form error). Sockets/cores have no error of their own - the
// sockets*cores>MAX_TOTAL_VCPU error is surfaced as a single line under the whole topology row
// (see the 'params[total_vcpu]' FormField below) - so it's applied manually here to still highlight
// both inputs. Not part of the library's public API - re-check on @plesk/ui-library upgrades.
const FORM_FIELD_ERROR_CLASS_NAME = 'pul-form-field--error';

const CpuTopologySeparator: React.FC<{ symbol: string }> = ({ symbol }) => (
    <Column width={24}>
        <FormField
            vertical
            label={<span style={{ visibility: 'hidden' }}>&nbsp;</span>}
        >
            <div style={{ ...CPU_TOPOLOGY_VALUE_STYLE, height: 28, justifyContent: 'center' }}>
                {symbol}
            </div>
        </FormField>
    </Column>
);

export interface IParamsColumnsProps {
    virtualizationType: VirtualizationType;
    minRam: number;
    ramUnit: string;
    setRamUnit: (unit: string) => void;
    minDisk?: number;
    fields?: string[];
    totalVcpu?: number;
}

export const ParamsColumns: React.FC<IParamsColumnsProps> = ({
    virtualizationType,
    minRam,
    ramUnit,
    setRamUnit,
    minDisk = 1,
    totalVcpu = 0,
    fields= [
        FIELDS.DISK,
        FIELDS.RAM,
        FIELDS.VCPU,
        FIELDS.VCPU_UNITS,
        FIELDS.VCPU_LIMIT,
        FIELDS.IO_PRIORITY,
        FIELDS.SWAP,
    ],
}) => {
    const isKvm = virtualizationType === VirtualizationType.KVM;

    const diskField = (
        <Column width={219}>
            <FormFieldNumber
                id="params[disk]"
                name="params[disk]"
                label={
                    <Translate content="plan.actionDialog.planDisk" />
                }
                vertical
                min={minDisk}
                size={SIZE.FILL}
            />
        </Column>
    );

    return (
        <>
            {fields.includes(FIELDS.VCPU) && isKvm && (
                // No control of its own - it wraps the topology row purely to read its own
                // sockets*cores>MAX_TOTAL_VCPU error (see PlanForm's 'params.total_vcpu' rule) via
                // fieldApi.getErrors(), so that error can both highlight sockets/cores below and
                // render as a single line spanning the whole row, instead of being squeezed
                // under one of the 130px-wide columns. Named 'total_vcpu' (a field that doesn't
                // exist for either virtualization type) rather than 'vcpu' - the VZ vcpu input
                // uses that same name, and would otherwise pick up this same stale error after
                // switching virtualization type away from KVM.
                <FormField name="params[total_vcpu]" label={null} vertical>
                    {(fieldApi) => {
                        const fieldErrors = fieldApi.getErrors();
                        const hasCpuTopologyLimitError = Boolean(fieldErrors && Object.keys(fieldErrors).length > 0);
                        const errorClassName = hasCpuTopologyLimitError ? FORM_FIELD_ERROR_CLASS_NAME : undefined;

                        return (
                            <Columns gap={SIZE.MD} vertical={false}>
                                <Column width={130}>
                                    <FormFieldNumber
                                        id="params[sockets]"
                                        name="params[sockets]"
                                        label={
                                            <Translate content="plan.actionDialog.planCpuSockets" />
                                        }
                                        fullDescription={
                                            <Translate content="plan.actionDialog.planCpuSocketsDescription" />
                                        }
                                        className={errorClassName}
                                        vertical
                                        min={1}
                                        max={MAX_TOTAL_VCPU}
                                        size={SIZE.FILL}
                                    />
                                </Column>
                                <CpuTopologySeparator symbol="×" />
                                <Column width={130}>
                                    <FormFieldNumber
                                        id="params[cores]"
                                        name="params[cores]"
                                        label={
                                            <Translate content="plan.actionDialog.planCpuCores" />
                                        }
                                        fullDescription={
                                            <Translate content="plan.actionDialog.planCpuCoresDescription" />
                                        }
                                        className={errorClassName}
                                        vertical
                                        min={1}
                                        max={MAX_TOTAL_VCPU}
                                        size={SIZE.FILL}
                                    />
                                </Column>
                                <CpuTopologySeparator symbol="=" />
                                <Column width={130}>
                                    <FormField
                                        label={
                                            <Translate content="plan.actionDialog.planCpuTotal" />
                                        }
                                        fullDescription={
                                            <Translate content="plan.actionDialog.planCpuTotalDescription" />
                                        }
                                        vertical
                                    >
                                        <div
                                            data-cy={FORM.VCPU_TOTAL}
                                            style={{ ...CPU_TOPOLOGY_VALUE_STYLE, minHeight: 28 }}
                                        >
                                            {totalVcpu}
                                        </div>
                                    </FormField>
                                </Column>
                            </Columns>
                        );
                    }}
                </FormField>
            )}
            {!isKvm && (
                <Columns gap={SIZE.MD} vertical={false}>
                    {fields.includes(FIELDS.VCPU) && (
                        <Column width={219}>
                            <FormFieldNumber
                                id="params[vcpu]"
                                name="params[vcpu]"
                                label={
                                    <Translate content="plan.actionDialog.planCpu" />
                                }
                                fullDescription={
                                    <Translate content="plan.actionDialog.planCpuDescription" />
                                }
                                vertical
                                min={1}
                                max={64}
                                size={SIZE.FILL}
                            />
                        </Column>
                    )}
                    {fields.includes(FIELDS.DISK) && diskField}
                </Columns>
            )}
            {fields.includes(FIELDS.DISK) && isKvm && (
                <Columns gap={SIZE.MD} vertical={false}>
                    {diskField}
                </Columns>
            )}
            {fields.includes(FIELDS.RAM) && (
                <Columns gap={SIZE.MD} vertical={false}>
                    <Column width={219}>
                        <FormFieldNumber
                            id="params[ram]"
                            name="params[ram]"
                            label={
                                <Translate content="plan.actionDialog.planRam" />
                            }
                            vertical
                            min={minRam}
                            size={SIZE.FILL}
                        />
                    </Column>
                    <Column width={150}>
                        <FormFieldSelect
                            id="ram_unit"
                            size={SIZE.XS}
                            label={
                                <Translate content="plan.actionDialog.planRamUnit" />
                            }
                            vertical
                            value={ramUnit}
                            onChange={setRamUnit}
                        >
                            {[DataUnit.MiB, DataUnit.GiB].map((value) => (
                                <option key={value} value={value}>
                                    {value}
                                </option>
                            ))}
                        </FormFieldSelect>
                    </Column>
                </Columns>
            )}
            <Columns gap={SIZE.MD} vertical={false}>
                {fields.includes(FIELDS.VCPU_UNITS) && (
                    <Column width={219}>
                        <FormFieldNumber
                            id="params[vcpu_units]"
                            name="params[vcpu_units]"
                            label={
                                <Translate content="plan.actionDialog.planCpuUnits" />
                            }
                            fullDescription={
                                <>
                                    <Translate content="plan.actionDialog.planCpuUnitsDescription" />
                                &nbsp;
                                    <a
                                        href="https://docs.virtuozzo.com/virtuozzo_hybrid_server_7_users_guide/managing-resources/managing-cpu-resources.html#configuring-cpu-units"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <Translate content="plan.actionDialog.documentation" />
                                    </a>
                                </>
                            }
                            vertical
                            min={8}
                            max={500000}
                            disabled={virtualizationType !== VirtualizationType.VZ}
                            size={SIZE.FILL}
                        />
                    </Column>
                )}
                {fields.includes(FIELDS.VCPU_LIMIT) && (
                    <Column width={219}>
                        <FormFieldNumber
                            id="params[vcpu_limit]"
                            name="params[vcpu_limit]"
                            label={
                                <Translate content="plan.actionDialog.planCpuLimits" />
                            }
                            fullDescription={
                                <>
                                    <Translate content="plan.actionDialog.planCpuLimitDescription" />
                                &nbsp;
                                    <a
                                        href="https://docs.virtuozzo.com/virtuozzo_hybrid_server_7_users_guide/managing-resources/managing-cpu-resources.html#configuring-cpu-limits-for-virtual-machines-and-containers"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <Translate content="plan.actionDialog.documentation" />
                                    </a>
                                </>
                            }
                            vertical
                            min={10}
                            max={6400}
                            disabled={virtualizationType !== VirtualizationType.VZ}
                            size={SIZE.FILL}
                        />
                    </Column>
                )}
            </Columns>
            <Columns gap={SIZE.MD} vertical={false}>
                {fields.includes(FIELDS.IO_PRIORITY) && (
                    <Column width={219}>
                        <FormFieldNumber
                            id="params[io_priority]"
                            name="params[io_priority]"
                            label={
                                <Translate content="plan.actionDialog.planIOPriority" />
                            }
                            fullDescription={
                                <>
                                    <Translate content="plan.actionDialog.planIOPriorityDescription" />
                                &nbsp;
                                    <a
                                        href="https://docs.virtuozzo.com/virtuozzo_hybrid_server_7_users_guide/managing-resources/managing-disk-io-parameters.html#configuring-priority-levels-for-virtual-machines-and-containers"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <Translate content="plan.actionDialog.documentation" />
                                    </a>
                                </>
                            }
                            vertical
                            min={0}
                            max={7}
                            disabled={virtualizationType !== VirtualizationType.VZ}
                            size={SIZE.FILL}
                        />
                    </Column>
                )}
                {fields.includes(FIELDS.SWAP) && (
                    <Column width={219}>
                        <FormFieldNumber
                            id="params[swap]"
                            name="params[swap]"
                            label={
                                <Translate content="plan.actionDialog.planSwap" />
                            }
                            vertical
                            min={0}
                            disabled={virtualizationType !== VirtualizationType.VZ}
                            size={SIZE.FILL}
                        />
                    </Column>
                )}
            </Columns>
        </>
    );
};

export default ParamsColumns;
